import { Alert, Badge, Card, Group, Stack, Table, Tabs, Text } from '@mantine/core';
import { useState } from 'react';
import HelpPopover from '../../HelpPopover/HelpPopover';
import { formatMs } from '../../KnowledgeBase/shared';
import AgentView from './AgentView';
import { TreeNode, usePicker } from './ResponseTree';
import { RuleDot, RULES } from './RulesPanel';
import { HELP } from './workspaceHelp';
import classes from './workspace.module.css';

const rowsLine = (rows) => {
  if (!rows) return null;
  const parts = [`${rows.total} rows`];
  if (rows.afterFilter !== rows.total) parts.push(`${rows.afterFilter} after the filter`);
  if (rows.droppedByMissingDay) parts.push(`${rows.droppedByMissingDay} dropped for a missing day`);
  parts.push(`${rows.afterMaxRows} to the agent`);
  return parts.join(' · ');
};

const RowsTable = ({ result, notFoundText, maxRows }) => {
  const rows = Array.isArray(result.mapped?.rows) ? result.mapped.rows : [];
  if (!rows.length)
    return (
      <Text size="sm" c="dimmed">
        {typeof result.mapped === 'string'
          ? `No rows. The agent hears: “${result.mapped}”`
          : `No rows.${result.rule === 'notFound' && notFoundText ? ` The agent hears: “${notFoundText}”` : ''}`}
      </Text>
    );

  const columns = [...new Set(rows.flatMap((row) => Object.keys(row)))];
  return (
    <Stack gap={4}>
      <Table.ScrollContainer minWidth={0}>
        <Table withTableBorder fz="xs" verticalSpacing={2}>
          <Table.Thead>
            <Table.Tr>
              {columns.map((column) => (
                <Table.Th key={column}>{column}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row, index) => (
              <Table.Tr key={index}>
                {columns.map((column) => (
                  <Table.Td key={column} ff="monospace">
                    {row[column] === undefined
                      ? ''
                      : typeof row[column] === 'object'
                        ? JSON.stringify(row[column])
                        : String(row[column])}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Text size="xs" c="dimmed">
        {rows.length} of {result.rows?.total ?? rows.length} rows · max {maxRows}
        {result.mapped.truncated && ' · shortened to fit the size limit'}
      </Text>
    </Stack>
  );
};

// The last Send (C7): status, the rule that fired, and three views of the answer. The response
// tree is also the picker for the Rows step.
// An example response (`result.example`) has a body only: it feeds the picker, shape only, until
// one real Send.
const ResponsePane = ({ result, sending, error, form, setField, readOnly, slots }) => {
  const [view, setView] = useState('response');
  const picker = usePicker(form, setField, result?.body, readOnly, slots);
  const example = !!result?.example;

  const head = (() => {
    if (sending)
      return (
        <Text size="sm" c="dimmed">
          Sending…
        </Text>
      );
    if (error)
      return (
        <Alert color="danger" variant="light">
          {error}
        </Alert>
      );
    if (!result)
      return (
        <Text size="sm" c="dimmed">
          Press Send to run the draft and see the response.
        </Text>
      );
    return null;
  })();

  return (
    <Stack gap="xs">
      {head || (
        <>
          {example ? (
            <Group gap="xs">
              <Badge variant="light" color="info" data-testid="send-status">
                shape only · send once before going live
              </Badge>
              <Text size="xs" c="dimmed">
                pasted example, not a call
              </Text>
            </Group>
          ) : (
            <>
              <Group gap="xs">
                <Badge variant="light" color={result.ok ? 'success' : 'danger'} data-testid="send-status">
                  {result.httpStatus != null ? `HTTP ${result.httpStatus}` : result.code || 'failed'}
                </Badge>
                <Badge variant="light" color="gray">
                  {formatMs(result.ms) || '—'}
                </Badge>
                <Text size="xs" c="dimmed">
                  ran your unsaved draft
                </Text>
              </Group>
              <Group gap="sm" data-testid="fired-rules">
                {RULES.map((rule) => (
                  <RuleDot key={rule.value} fired={result.rule === rule.value}>
                    {rule.label}
                  </RuleDot>
                ))}
              </Group>
              {result.rows && <Text size="sm">{rowsLine(result.rows)}</Text>}
              {result.message && (
                <Text size="xs" c="dimmed">
                  {result.message}
                </Text>
              )}
            </>
          )}

          <Tabs
            value={example ? 'response' : view}
            onChange={(next) => setView(next || 'response')}
            keepMounted={false}
          >
            <Tabs.List>
              <Tabs.Tab value="response">Response</Tabs.Tab>
              {!example && <Tabs.Tab value="rows">Rows</Tabs.Tab>}
              {!example && <Tabs.Tab value="agent">What the agent sees</Tabs.Tab>}
            </Tabs.List>

            <Tabs.Panel value="response" pt="xs">
              {result.body === undefined || result.body === null ? (
                <Text size="xs" c="dimmed">
                  No body to show.
                </Text>
              ) : (
                <Card withBorder padding={0}>
                  {!readOnly && (
                    <Group gap={4} px="xs" pt={4}>
                      <Text size="xs" c="dimmed">
                        {slots
                          ? 'Click a list to use it as rows, a value to fill the selected slot.'
                          : 'Click a list to use it as rows, a value to add it as a field.'}
                      </Text>
                      <HelpPopover {...HELP.picker} onUse={picker.useRootExample} />
                    </Group>
                  )}
                  <div className={classes.tree} style={{ border: 0 }}>
                    <TreeNode value={result.body} segments={[]} depth={0} picker={picker} />
                  </div>
                </Card>
              )}
              {result.truncated && (
                <Text size="xs" c="dimmed" mt={4}>
                  Long lists are shortened here; the call itself read them whole.
                </Text>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="rows" pt="xs">
              <RowsTable result={result} notFoundText={form.tool.notFoundText} maxRows={form.tool.maxRows} />
            </Tabs.Panel>

            <Tabs.Panel value="agent" pt="xs">
              <AgentView result={result} />
            </Tabs.Panel>
          </Tabs>
        </>
      )}
    </Stack>
  );
};

export default ResponsePane;
